import React from "react";
import { useLocation, useHistory } from "react-router-dom";
import { Box, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useSidebarAccent } from "../SidebarAccentContext";

const DrawerListItem = ({ path, name, Icon, badge, exact = false }) => {
  const location = useLocation();
  const router   = useHistory();
  const theme    = useTheme();
  const { accent, minimized } = useSidebarAccent();

  const target   = `/${path}`;
  const isActive = location.pathname === target ||
                   (!exact && location.pathname.startsWith(target + "/"));

  return (
    <Tooltip title={name} placement="right" disableInteractive>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => router.push(target)}
        onKeyDown={(e) => e.key === "Enter" && router.push(target)}
        sx={{
          display:        "flex",
          alignItems:     "center",
          justifyContent: minimized ? "center" : "flex-start",
          gap:            minimized ? 0 : 1.25,
          mx:             1,
          my:             0.25,
          px:             minimized ? 0 : 1.25,
          py:             0.875,
          borderRadius:   1,
          cursor:         "pointer",
          outline:        "none",
          userSelect:     "none",
          position:       "relative",
          bgcolor:        isActive ? alpha(accent, 0.12) : "transparent",
          color:          isActive ? theme.palette.text.primary : theme.palette.text.secondary,
          border:         `1px solid ${isActive ? alpha(accent, 0.25) : "transparent"}`,
          transition:     "all 0.15s ease",

          "&:hover": {
            bgcolor: isActive ? alpha(accent, 0.16) : alpha(theme.palette.text.primary, 0.05),
            color:   theme.palette.text.primary,
            "& .nav-icon": { color: isActive ? accent : theme.palette.text.primary },
          },

          "&:focus-visible": {
            outline:       `2px solid ${accent}`,
            outlineOffset: 1,
          },
        }}
      >
        {/* Icon */}
        <Box
          className="nav-icon"
          sx={{
            display:    "flex",
            alignItems: "center",
            flexShrink: 0,
            color:      isActive ? accent : theme.palette.text.secondary,
            transition: "color 0.15s ease",
          }}
        >
          <Icon sx={{ fontSize: 18 }} />
        </Box>

        {/* Label + badge — hidden when minimized */}
        {!minimized && (
          <>
            <Typography
              sx={{
                flex:          1,
                fontSize:      "0.875rem",
                fontWeight:    isActive ? 600 : 400,
                lineHeight:    1.3,
                letterSpacing: "0.005em",
                whiteSpace:    "nowrap",
                overflow:      "hidden",
                textOverflow:  "ellipsis",
              }}
            >
              {name}
            </Typography>

            {badge != null && badge > 0 && (
              <Box
                sx={{
                  minWidth:       18,
                  height:         18,
                  px:             0.75,
                  borderRadius:   3,
                  bgcolor:        accent,
                  color:          "#fff",
                  fontSize:       "0.65rem",
                  fontWeight:     700,
                  display:        "flex",
                  alignItems:     "center",
                  justifyContent: "center",
                  flexShrink:     0,
                }}
              >
                {badge > 99 ? "99+" : badge}
              </Box>
            )}
          </>
        )}
      </Box>
    </Tooltip>
  );
};

export default DrawerListItem;
